import React from "react";
import * as Mui from "@mui/material";
import { AddDialog } from "./add-dialog"
import { AddAgentDialog } from "./add-agent-dialog"
import { ConfirmationModal } from "../confirmation-modal"
import { RootState } from "redux/store";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { GeographicRoute, NewGeographicRoute } from "model/geographic-route";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { Table } from "component/shared/table";
import { Avatar } from "component/shared/avatar";
import { getUserUnsafe } from "redux/selector";
import { FeatherIcon } from "component/shared/feather-icon";
import { Agent } from "model/agent";
import { Switch } from "component/shared/switch";
import { saveUser } from "redux/slice/authentication";
import { UserPartial } from "model/user";
import { deleteGeographicRoute, saveGeographicRoute } from "redux/slice/geographic-routes";
import { Routes } from "./routes";
import * as env from "shared/env";
import { baseColors } from "theme";
import { urls } from "routes/urls";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";

interface Props	extends OwnProps,
	ReturnType<typeof mapDispatchToProps>,
	ReturnType<typeof mapStateToProps>,
	Router.RouteComponentProps {

}

const mapStateToProps = (state: RootState) => {
	const user = getUserUnsafe(state);
	return {
		user,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	saveUser,
	deleteGeographicRoute,
	saveGeographicRoute,
}, dispatch);

interface OwnProps {
	geographicRoutes: GeographicRoute[];
	checked?: boolean;
	onToggle?: (checked: boolean) => void;
}

interface State {
	dialogOpen: boolean;
	goegraphicRoute: GeographicRoute | null;
	editAgentRoute: { agent: Agent; routes: GeographicRoute[] } | null;
	removeAgentRoute: { agent: Agent; routes: GeographicRoute[] } | null;
	enabled: boolean;
	rowEdit: number;
}

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			dialogOpen: false,
			goegraphicRoute: null,
			editAgentRoute: null,
			removeAgentRoute: null,
			enabled: props.user && props.user.geographicRouting ? true : false,
			rowEdit: -1,
		};
	}

	private getAgentRoutes = () => {
		const { geographicRoutes } = this.props;
		const agents = geographicRoutes.reduce<Agent[]>((agents, route) => {
			const agentId = route.agent.id;
			const index = agents.findIndex((agent) => agent.id === agentId)
			if (index === -1) {
				agents.push(route.agent);
			}
			return agents;
		}, []);
		return agents.map((agent) => {
			const routes = geographicRoutes.filter((route) => route.agent.id === agent.id)
			return {
				agent: agent,
				routes: routes,
			};
		});
	}

	private saveUser() {
		const { saveUser } = this.props;
		const { enabled } = this.state;
		const user: UserPartial = {
			geographicRouting: enabled,
		}
		saveUser({user});
	}

	public render() {
		const { saveGeographicRoute, deleteGeographicRoute, geographicRoutes, onToggle } = this.props;
		const { dialogOpen, rowEdit } = this.state;
		const controlled = onToggle !== undefined;
		const enabled = controlled ? !!this.props.checked : this.state.enabled;
		const showOptions = !env.refreshDesignsDev || enabled;
		const agentRoutes = this.getAgentRoutes();

		const onCreateRoute = (agentRoute: { agent: Agent }) => (postalCode: string) => {
			const geographicRoute: NewGeographicRoute = { agent: agentRoute.agent, postalCode };
			saveGeographicRoute({ geographicRoute });
		};
		const onDeleteRoute = (agentRoute: { agent: Agent }) => (route: GeographicRoute) => {
			if (window.confirm(`Delete postal code ${route.postalCode} for ${agentRoute.agent.label}?`)) {
				deleteGeographicRoute({ geographicRoute: route });
			}
		};

		return (
			<>
				<Mui.Grid container direction="column" spacing={2}>
					{!env.refreshDesignsDev && (
						<Mui.Grid item>
							<Mui.Typography>
							Route all new leads on properties in a given postal code to a single agent.
							Agents can have multiple postal codes assigned to them, but each postal code may be assigned to only one agent.
							</Mui.Typography>
						</Mui.Grid>
					)}
					<Mui.Grid item>
						<Switch
							checked={enabled}
							onChange={controlled ? onToggle! : (checked) => {
								this.setState({enabled: checked}, () => this.saveUser());
							}}
							label={env.refreshDesignsDev ? "Assign leads based on listing postal code" : "Enabled"}
							description={env.refreshDesignsDev ? "When enabled, leads are assigned to agents based on the postal code of the listing they inquire about. An agent can be assigned multiple postal codes, but each postal code can only be assigned to one agent." : undefined}
						/>
					</Mui.Grid>
					{showOptions && (env.refreshDesignsDev ? (
						<Mui.Grid item style={{ marginLeft: 59 }}>
							<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, maxWidth: 960 }}>
								<Mui.Typography style={{ fontWeight: "bold", fontSize: 16 }}>Assigned Postal Codes</Mui.Typography>
								<Mui.Button
									variant="contained"
									color="primary"
									onClick={() => this.setState({ dialogOpen: true, editAgentRoute: null })}
									startIcon={<FeatherIcon fontSize="small"><Icons.Plus /></FeatherIcon>}
								>
									Add Agent
								</Mui.Button>
							</div>
							<Mui.ClickAwayListener onClickAway={() => this.setState({ rowEdit: -1 })}>
								<Mui.TableContainer component={Mui.Paper} style={{ boxShadow: "none", border: `1px solid ${baseColors.gray100}`, borderRadius: 8, maxWidth: 960 }}>
									<Mui.Table>
										<Mui.TableHead>
											<Mui.TableRow>
												<Mui.TableCell style={{ backgroundColor: baseColors.gray50, color: baseColors.gray, fontWeight: 400 }}>Agent Name</Mui.TableCell>
												<Mui.TableCell style={{ backgroundColor: baseColors.gray50, color: baseColors.gray, fontWeight: 400 }}>Postal Codes</Mui.TableCell>
											</Mui.TableRow>
										</Mui.TableHead>
										<Mui.TableBody>
											{agentRoutes.length === 0 ? (
												<Mui.TableRow>
													<Mui.TableCell colSpan={2}>
														<Mui.Typography style={{ fontStyle: "italic", color: baseColors.gray }}>No agents added yet!</Mui.Typography>
													</Mui.TableCell>
												</Mui.TableRow>
											) : agentRoutes.map((agentRoute, row) => (
												<Mui.TableRow key={agentRoute.agent.id}>
													<Mui.TableCell>
														<Mui.Link
															component={RouterLinkWrapper}
															to={urls.settingsAgent(agentRoute.agent)}
															color="primary"
														>
															{agentRoute.agent.label}
														</Mui.Link>
														{agentRoute.agent.email && (
															<Mui.Typography variant="body2" color="textSecondary">
																{agentRoute.agent.email}
															</Mui.Typography>
														)}
													</Mui.TableCell>
													<Mui.TableCell>
														<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
															<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
																{agentRoute.routes.map((route) => (
																	<Mui.Chip key={route.postalCode} label={route.postalCode} style={{ backgroundColor: baseColors.gray100 }} />
																))}
															</div>
															<div style={{ whiteSpace: "nowrap" }}>
																<Mui.IconButton size="small" color="primary" onClick={() => this.setState({ dialogOpen: true, editAgentRoute: agentRoute })}>
																	<FeatherIcon fontSize="small"><Icons.Edit /></FeatherIcon>
																</Mui.IconButton>
																<Mui.Tooltip title="Remove Rule">
																	<Mui.IconButton size="small" color="primary" style={{ marginLeft: 8 }} onClick={() => this.setState({ removeAgentRoute: agentRoute })}>
																		<FeatherIcon fontSize="small"><Icons.Trash /></FeatherIcon>
																	</Mui.IconButton>
																</Mui.Tooltip>
															</div>
														</div>
													</Mui.TableCell>
												</Mui.TableRow>
											))}
										</Mui.TableBody>
									</Mui.Table>
								</Mui.TableContainer>
							</Mui.ClickAwayListener>
						</Mui.Grid>
					) : (
						<>
						<Mui.Grid item>
							<Mui.Typography variant="h4">
								Routing Rules
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item style={!enabled ? {pointerEvents: "none", opacity: .5}: {}}>
							<Mui.Button
								variant="contained"
								color={"primary"}
								onClick={() => this.setState({ dialogOpen: true})}
								startIcon={
									<FeatherIcon>
										<Icons.PlusCircle />
									</FeatherIcon>
								}
							>
									Add
							</Mui.Button>
						</Mui.Grid>
						<Mui.Grid item style={!enabled ? {opacity: .5}: {}}>
							<Mui.ClickAwayListener onClickAway={() => this.setState({ rowEdit: -1 })} >
								<Table
									hidePagination={agentRoutes.length < 10}
									items={agentRoutes}
									columns={[
										{
											id: "avatar",
											width: 36,
											value: (agentRoute) => {
												return (
													<Avatar
														phrase={agentRoute.agent.label || ""}
														src={agentRoute.agent.photoUrl}
													/>
												);
											},
										},
										{
											id: "label",
											width: 200,
											value: (agentRoute) => {
												return (
													<Mui.Typography>
														{agentRoute.agent.label}
													</Mui.Typography>
												);
											},
										},
										{
											id: "postlcodes",
											value: (agentRoute, column, row) => {
												return (
													<Routes
														rowEdit={rowEdit}
														row={row}
														disabledOptions={geographicRoutes}
														geographicRoutes={agentRoute.routes}
														onCreate={onCreateRoute(agentRoute)}
														onDelete={onDeleteRoute(agentRoute)}
														onToggle={(value) => this.setState({rowEdit: value})}
													/>
												);
											},
										},
										{
											id: "actions",
											showOnHover: true,
											width: 150,
											align: "right",
											value: (agentRoute, column, row) => {
												return (
													<>
														<Mui.IconButton
															onClick={() => {
																this.setState({rowEdit: rowEdit < 0 ? row : -1});
															}}
														>
															<FeatherIcon>
																{rowEdit === row ? (
																	<Icons.Edit3 />
																) : (
																	<Icons.Edit2 />
																)}
															</FeatherIcon>
														</Mui.IconButton>
														<Mui.Tooltip title="Remove Rule">
															<Mui.IconButton
																onClick={() => {
																	if (window.confirm(`Delete geographic routes for ${agentRoute.agent.label}?`)) {
																		agentRoute.routes.forEach((geographicRoute) => {
																			deleteGeographicRoute({geographicRoute});
																		})
																	}
																}}
															>
																<FeatherIcon>
																	<Icons.Trash />
																</FeatherIcon>
															</Mui.IconButton>
														</Mui.Tooltip>
													</>
												);
											},
										}
									]}
								/>
							</Mui.ClickAwayListener>
						</Mui.Grid>
						</>
					))}
				</Mui.Grid>
				{env.refreshDesignsDev ? (
					<AddAgentDialog
						open={dialogOpen}
						agentRoutes={agentRoutes}
						editAgentRoute={this.state.editAgentRoute}
						onClose={() => this.setState({ dialogOpen: false, editAgentRoute: null })}
						onSave={(geographicRoute) => saveGeographicRoute({geographicRoute})}
						onDelete={(geographicRoute) => deleteGeographicRoute({geographicRoute})}
					/>
				) : (
					<AddDialog
						open={dialogOpen}
						agentRoutes={agentRoutes}
						onClose={() => this.setState({ dialogOpen: false })}
						onSave={(geographicRoute) => saveGeographicRoute({geographicRoute})}
					/>
				)}
				{env.refreshDesignsDev && this.state.removeAgentRoute && (
					<ConfirmationModal
						open={true}
						title="Remove agent from Geographic Routing?"
						confirmLabel="Remove Agent"
						body={
							<Mui.Typography variant="body1">
								This agent and their assigned postal codes will be removed. Leads in those areas will fall back to your other routing rules.
							</Mui.Typography>
						}
						onCancel={() => this.setState({ removeAgentRoute: null })}
						onConfirm={() => {
							this.state.removeAgentRoute?.routes.forEach((geographicRoute) => deleteGeographicRoute({ geographicRoute }));
							this.setState({ removeAgentRoute: null });
						}}
					/>
				)}
			</>
		);
	}
}

export const GeographicTab = (
	Router.withRouter(
		connect(mapStateToProps, mapDispatchToProps)(Component)
	)
);
